import { z } from 'zod';

const num = () => z.coerce.number();
const numNull = () => z.union([z.coerce.number(), z.null()]);

export const OnlinePerformanceDailySchema = z.array(z.object({
  date: z.string(),
  brand_name: z.string(),
  brand_manager: z.string().nullable(),
  merch_portfolio: z.string().nullable(),
  ordered_items: num(),
  delivered_items: num(),
  cancelled_items: num(),
  rtoed_items: num(),
  returned_items: num(),
  gross_gmv: num(),
  /* Gross GMV of orders not in CANCELLED / RTO / RETURNED / RETURN_WIP /
     UNFULFILLABLE status, excluding exchanges. Distinct from "Net GMV"
     (a different merch metric). */
  gross_gmv_net_rc: num(),
  mto_items: num(),
  onhand_items: num(),
  jit_items: num(),
  /* MTC split (added 2026-06-10, card 8129). Optional so older cached
     responses still parse. */
  mtc_items: num().optional().default(0),
  mto_gmv: num(),
  onhand_gmv: num(),
  jit_gmv: num(),
  mtc_gmv: num().optional().default(0),
  spend: num(),
  gross_roas: numNull(),
  net_rc_roas: numNull(),
  /* Halo ROAS components (added 2026-07-16, card 8129). Meta-attributed
     purchase value (meta_gmv) and Meta ad spend (meta_spend) per brand-day.
     Optional so older cached responses still parse; halo_roas is derived
     frontend-side as sum(meta_gmv) / sum(meta_spend). */
  meta_gmv: num().optional().default(0),
  meta_spend: num().optional().default(0),
}));
export type OnlinePerformanceDaily = z.infer<typeof OnlinePerformanceDailySchema>;

/* Product-page traffic by brand × source (card 10118). CUBE rows: is_all_brands /
   is_all_sources mark rolled-up rows (brand_name / source null there), so unique
   users and styles are exact at every level and must never be summed. */
const flag = () => z.union([z.boolean(), z.coerce.number()]).transform(v => Boolean(v));
export const OnlineTrafficBrandSourceSchema = z.array(z.object({
  is_all_brands: flag(),
  is_all_sources: flag(),
  brand_name: z.string().nullable(),
  source: z.string().nullable(),
  brand_manager: z.string().nullable(),
  merch_portfolio: z.string().nullable(),
  pdp_views: num(),
  users: num(),
  styles_viewed: num(),
  live_styles_viewed: num(),
}));
export type OnlineTrafficBrandSource = z.infer<typeof OnlineTrafficBrandSourceSchema>;

/* Top 25 styles per brand by product-page views (card 10119), with per-source
   views / users and online items ordered in the window. */
export const OnlineTopTrafficStylesSchema = z.array(z.object({
  lk_style_number: z.string(),
  brand_name: z.string(),
  brand_manager: z.string().nullable(),
  merch_portfolio: z.string().nullable(),
  product_name: z.string().nullable(),
  category: z.string().nullable(),
  mrp: numNull(),
  image_url: z.string().nullable(),
  store_url: z.string().nullable(),
  is_live: flag(),
  pdp_views: num(),
  users: num(),
  views_app: num(), users_app: num(),
  views_meta_ads: num(), users_meta_ads: num(),
  views_meta_unclassified: num(), users_meta_unclassified: num(),
  views_google: num(), users_google: num(),
  views_instagram: num(), users_instagram: num(),
  views_crm: num(), users_crm: num(),
  views_direct: num(), users_direct: num(),
  views_other: num(), users_other: num(),
  items_ordered: num(),
}));
export type OnlineTopTrafficStyles = z.infer<typeof OnlineTopTrafficStylesSchema>;

/* Live styles per brand, current snapshot (card 10120). */
export const OnlineLiveStylesSchema = z.array(z.object({
  brand_name: z.string(),
  brand_manager: z.string().nullable(),
  merch_portfolio: z.string().nullable(),
  live_styles: num(),
}));
export type OnlineLiveStyles = z.infer<typeof OnlineLiveStylesSchema>;

/* Online add-to-carts per brand (card 10126): cart item adds (removed items
   still count) and distinct customers who added; logged-in carts only. */
export const OnlineAddToCartsSchema = z.array(z.object({
  brand_name: z.string(),
  brand_manager: z.string().nullable(),
  merch_portfolio: z.string().nullable(),
  atc_items: num(),
  atc_users: num(),
}));
export type OnlineAddToCarts = z.infer<typeof OnlineAddToCartsSchema>;
